import { startTransition, useEffect } from 'react';

import { queryErrorEntry, useDelayedContinuousQueryError } from '@/hooks/useDelayedContinuousQueryError';
import { useGetConflicten } from '@/queries/conflicten/useGetConflicten';
import useGetDefectenWithLocation from '@/queries/defecten/useGetDefectenWithLocation';
import { envVariables } from '@/utils/env';
import useGetEenhedenStatus from '@/views/ddp/hooks/useGetEenhedenStatus';

import { derriveReserveFromReserveV2 } from '../utils/reserve';

import useGetNotities from './useGetNotities';
import useGetReserves from './useGetReserves';
import useGetReservesV2 from './useGetReservesV2';
import useGetVoorraad from './useGetVoorraad';
import usePlankTableStore from './usePlankTableStore';

/** usePlankTable initializes the DDP with data added to the store.
 *
 * @returns
 */
const usePlankTable = () => {
  const setVoorraad = usePlankTableStore((state) => state.setVoorraad);
  const setConflicten = usePlankTableStore((state) => state.setConflicten);
  const setEenhedenStatus = usePlankTableStore((state) => state.setEenhedenStatus);
  const setReserves = usePlankTableStore((state) => state.setReserves);
  const setDefecten = usePlankTableStore((state) => state.setDefecten);
  const setNotities = usePlankTableStore((state) => state.setNotities);

  const featureReservesV2 = !!envVariables().featureReservesV2;

  const voorraadQuery = useGetVoorraad();
  const defectenWithLocationQuery = useGetDefectenWithLocation({ days: 2 }, 10_000); // If number of days changes, change the equivelent of hours in domain/defect/defect.ts
  const conflictenQuery = useGetConflicten(10_000, { throwOnHttpError: true, suppressGlobalErrorToast: true });
  const reservesV1Query = useGetReserves(!featureReservesV2);
  const reservesV2Query = useGetReservesV2(featureReservesV2);
  const reservesQuery = featureReservesV2 ? reservesV2Query : reservesV1Query;
  const reserves = featureReservesV2 ? reservesV2Query.data?.map(derriveReserveFromReserveV2) : reservesV1Query.data;
  const notitiesQuery = useGetNotities();
  const eenhedenStatusQuery = useGetEenhedenStatus();
  const { isBlockingError } = useDelayedContinuousQueryError({
    scope: 'plank',
    criticalQueries: [queryErrorEntry('voorraad', voorraadQuery)],
    supplementalQueries: [
      queryErrorEntry('defecten-met-locatie', defectenWithLocationQuery),
      queryErrorEntry('conflicten', conflictenQuery),
      queryErrorEntry('reserves', reservesQuery),
      queryErrorEntry('notities', notitiesQuery),
      queryErrorEntry('eenheden-status', eenhedenStatusQuery),
    ],
  });

  useEffect(() => {
    if (!eenhedenStatusQuery.isSuccess || eenhedenStatusQuery.data === undefined) {
      return;
    }
    setEenhedenStatus(eenhedenStatusQuery.data);
  }, [eenhedenStatusQuery.data, eenhedenStatusQuery.isSuccess, setEenhedenStatus]);

  useEffect(() => {
    if (!defectenWithLocationQuery.isSuccess || defectenWithLocationQuery.data === undefined) {
      return;
    }
    startTransition(() => {
      const { defecten, locatieVoorEenheden } = defectenWithLocationQuery.data;
      setDefecten(defecten, locatieVoorEenheden);
    });
  }, [defectenWithLocationQuery.data, defectenWithLocationQuery.isSuccess, setDefecten]);

  useEffect(() => {
    if (!conflictenQuery.isSuccess || conflictenQuery.data === undefined) {
      return;
    }
    startTransition(() => {
      setConflicten(conflictenQuery.data);
    });
    // Ook herberekenen zodra eenhedenStatus binnenkomt/verandert: setConflicten filtert op eenheidStatus uit de
    // store, maar draait pas opnieuw bij een nieuwe conflicten-poll. Zonder deze dependency kan de mancolijst na het
    // wisselen naar de plank tijdelijk te lang zijn (ongefilterd) totdat de volgende conflicten-poll plaatsvindt.
  }, [conflictenQuery.data, conflictenQuery.isSuccess, eenhedenStatusQuery.data, setConflicten]);

  useEffect(() => {
    if (!reservesQuery.isSuccess || reserves === undefined) {
      return;
    }
    startTransition(() => {
      setReserves(reserves);
    });
  }, [reserves, reservesQuery.isSuccess, setReserves]);

  useEffect(() => {
    if (!voorraadQuery.isSuccess || voorraadQuery.data === undefined) {
      return;
    }
    startTransition(() => {
      setVoorraad(voorraadQuery.data);
    });
  }, [voorraadQuery.data, voorraadQuery.isSuccess, setVoorraad]);

  useEffect(() => {
    if (!notitiesQuery.isSuccess || notitiesQuery.data === undefined) {
      return;
    }
    startTransition(() => {
      setNotities(notitiesQuery.data);
    });
  }, [notitiesQuery.data, notitiesQuery.isSuccess, setNotities]);

  return {
    isBlockingError,
    refetchCritical: voorraadQuery.refetch,
  };
};
export default usePlankTable;
